import React, { useMemo, useState } from "react";

import { Button, FormControl, IconButton, MenuItem } from "@mui/material";
import withStyles from "@mui/styles/withStyles";
import classnames from "classnames";
import { IoIosArrowBack, IoIosArrowForward } from "react-icons/io";

import { useTranslation } from "react-i18next";
import Select from "../components/Generic/Select";
import { getBrandCustom } from "../utils/getCustomColor";
import useStyles from "./styles";
import { colors } from "@zapalacorp/tokens";

type Props = {
  currentPage?: number;
  handleChangePage: Function;
  pageSize: number;
  pageSizeList?: number[];
  onChangePageSize?: Function;
  totalItems: number;
  namePage?: string;
  footerStyle?: React.CSSProperties;
  showFooterPageInfo: boolean;
  showFooterPaginationInfo?: boolean;
  alwaysShowFooter?: boolean;
  onGoToPage?: (page: number) => void;
};

const FormControlCustomized = withStyles(() => ({
  root: {
    margin: "0 15px",
    minWidth: "120px",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: getBrandCustom("primaryColor", colors.brandBlue500),
    },
    "&:active .MuiOutlinedInput-notchedOutline": {
      borderColor: getBrandCustom("primaryColor", colors.brandBlue500),
    },
  },
}))(FormControl);

const GridFooter = ({
  currentPage,
  pageSize,
  pageSizeList,
  handleChangePage,
  totalItems,
  namePage,
  onChangePageSize,
  showFooterPageInfo,
  showFooterPaginationInfo,
  alwaysShowFooter,
  onGoToPage,
  footerStyle,
}: Props) => {
  const classes = useStyles();
  const { t: translate, i18n } = useTranslation();

  const [numPage, setNumPage] = useState(currentPage || 1);

  const totalPages = useMemo(() => {
    if (Number.isInteger(totalItems / pageSize)) {
      return totalItems / pageSize;
    }

    return parseInt((totalItems / pageSize).toString()) + 1;
  }, [totalItems, pageSize]);

  const handleChange = (option: string, goto = 0) => {
    switch (option) {
      case "previous":
        if (numPage > 1) {
          setNumPage(numPage - 1);
          handleChangePage(option, numPage - 1);
        }
        break;
      case "next":
        if (numPage < totalPages) {
          setNumPage(numPage + 1);
          handleChangePage(option, numPage + 1);
        }
        break;
      case "goto":
        if (goto > 0 && goto <= totalPages) {
          setNumPage(goto);
          handleChangePage(option, goto);
        }
        break;
      case "first":
        setNumPage(1);
        handleChangePage(option, 1);
        break;
      case "last":
        setNumPage(totalPages);
        handleChangePage(option, totalPages);
        break;
      default:
        break;
    }
  };

  const renderSelectPageSize = () => {
    if (pageSizeList && pageSizeList?.length >= 1) {
      return (
        <div data-cy="footer-page-size-list" className={classes.footerPageSize}>
          <span className={classes.textFooterShowInfo}>&nbsp; | &nbsp;{translate("display")} </span>
          <FormControlCustomized data-cy="footer-form-control-select-page-size" variant="outlined">
            <Select
              className={classes.select}
              data-cy="footer-select-page-size"
              value={pageSize}
              onChange={(event) => {
                if (onChangePageSize) {
                  onChangePageSize(Number(event.target.value));

                  if (onGoToPage) {
                    setNumPage(1);
                    onGoToPage(1);
                  }
                }
              }}
            >
              {pageSizeList.map((pageSize) => (
                <MenuItem data-cy={pageSize.toString()} key={pageSize} value={pageSize}>
                  <span className={classes.textSelect}>{pageSize}</span>
                </MenuItem>
              ))}
            </Select>
          </FormControlCustomized>
          <span className={classes.textFooterShowInfo} data-cy="footer-page-size-list-name-page">
            {namePage
              ? typeof namePage === "string"
                ? namePage.substr(0, 1).toUpperCase() + namePage.substr(1).toLowerCase()
                : namePage
              : ""}
          </span>
        </div>
      );
    }

    return null;
  };

  const renderPreviousButton = () => {
    return (
      <IconButton
        data-cy="footer-previous-button"
        size="small"
        disabled={numPage === 1}
        className={classnames(classes.buttonPage, classes.buttonPageFirst)}
        onClick={() => handleChange("previous")}
      >
        <IoIosArrowBack size={20} color={numPage === 1 ? "#E4E7EC" : "#667085"} />
      </IconButton>
    );
  };

  const renderNextPage = () => {
    return (
      <IconButton
        size="small"
        disabled={numPage === totalPages}
        className={classnames(classes.buttonPage, classes.buttonPageNext)}
        onClick={() => handleChange("next")}
        data-cy="footer-next-button"
      >
        <IoIosArrowForward size={20} color={numPage === totalPages ? "#E4E7EC" : "#667085"} />
      </IconButton>
    );
  };

  const renderFooterPageInfo = () => {
    if (showFooterPageInfo) {
      return (
        <div data-cy="footer-info" className={classes.footerShowInfo}>
          <span className={classes.textFooterShowInfo}>
            {translate("showing")} {(numPage - 1) * pageSize + 1} -{" "}
            {pageSize * numPage > totalItems ? totalItems : pageSize * numPage} {translate("of")}{" "}
            {totalItems.toLocaleString(i18n.language)} {namePage && namePage.toLowerCase()}
          </span>
          {renderSelectPageSize()}
        </div>
      );
    }

    return null;
  };

  const renderFooterPaginationButtons = () => {
    if (!showFooterPaginationInfo) return;

    return (
      <div data-cy="footer-options" className={classes.gridFooterOptions}>
        {renderPreviousButton()}
        {numPage !== 1 && numPage - 1 !== 1 && numPage - 2 !== 1 && numPage - 3 !== 1 && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("first")}
            data-cy="footer-button-first"
          >
            1
          </Button>
        )}
        {numPage !== 1 && numPage - 1 !== 1 && numPage - 2 !== 1 && numPage - 3 !== 1 && (
          <Button disabled data-cy="footer-button-ellipsis-first" className={classes.buttonPage}>
            ...
          </Button>
        )}

        {numPage - 3 > 0 && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage - 3)}
            data-cy={`footer-button-${numPage - 3}`}
          >
            {numPage - 3}
          </Button>
        )}
        {numPage - 2 > 0 && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage - 2)}
            data-cy={`footer-button-${numPage - 2}`}
          >
            {numPage - 2}
          </Button>
        )}
        {numPage - 1 > 0 && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage - 1)}
            data-cy={`footer-button-${numPage - 1}`}
          >
            {numPage - 1}
          </Button>
        )}

        {numPage <= totalPages && (
          <Button
            className={classnames(classes.buttonPage, classes.buttonSelected)}
            data-cy={`footer-button-${numPage}`}
          >
            {numPage}
          </Button>
        )}

        {numPage + 1 <= totalPages && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage + 1)}
            data-cy={`footer-button-${numPage + 1}`}
          >
            {numPage + 1}
          </Button>
        )}
        {numPage + 2 <= totalPages && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage + 2)}
            data-cy={`footer-button-${numPage + 2}`}
          >
            {numPage + 2}
          </Button>
        )}
        {numPage + 3 <= totalPages && numPage + 3 !== totalPages - 1 && (
          <Button
            className={classes.buttonPage}
            onClick={() => handleChange("goto", numPage + 3)}
            data-cy={`footer-button-${numPage + 3}`}
          >
            {numPage + 3}
          </Button>
        )}

        {numPage !== totalPages &&
          numPage + 1 !== totalPages &&
          numPage + 2 !== totalPages &&
          numPage + 3 !== totalPages && (
            <Button disabled className={classes.buttonPage} data-cy="footer-button-ellipsis-second">
              ...
            </Button>
          )}
        {numPage !== totalPages &&
          numPage + 1 !== totalPages &&
          numPage + 2 !== totalPages &&
          numPage + 3 !== totalPages && (
            <Button
              className={classes.buttonPage}
              onClick={() => handleChange("last")}
              data-cy="footer-button-total-pages"
            >
              {totalPages}
            </Button>
          )}
        {renderNextPage()}
      </div>
    );
  };

  if (
    (totalPages < 2 && !alwaysShowFooter) ||
    (!showFooterPageInfo && !showFooterPaginationInfo) ||
    (!totalPages || totalPages <= 0)
  ) {
    return null;
  }

  return (
    <div data-cy="footer" className={classes.gridFooter} style={footerStyle}>
      {renderFooterPageInfo()}
      {renderFooterPaginationButtons()}
    </div>
  );
};

export default GridFooter;
